<script setup lang="ts">
import { ElIcon } from 'element-plus'
import { computed, inject, ref, watch } from 'vue'

import { BoxComponent, ButtonComponent, CarouselSlider, FlexComponent, PlatformHandle, TooltipComponent, TypographyComponent } from '../..'
import DownloadIcon from '../../../assets/icons/download.svg?component'
import ExternalLinkIcon from '../../../assets/icons/external-link.svg?component'
import HelpIcon from '../../../assets/icons/help.svg?component'
import CloseIcon from '../../../assets/icons/remove.svg?component'
import ThumbsDownIcon from '../../../assets/icons/thumb-down.svg?component'
import ThumbsUpIcon from '../../../assets/icons/thumb.svg?component'
import { formatterProviderKey, linksProviderKey } from '../../../constants'
import { defaultFormatter, formatLongValue } from '../../../formatters'
import { useMobileView } from '../../../hooks'
import type { AffiliateLinkMetrics, ContentItemLinkDetails } from '../../../types'
import { ContentItemStatusActionEnum, ContentItemStatusEnum } from '../../../types'
import type { Asset } from '../../../types/common.types'
import { deriveAffiliateLinkKpis, isInternalLink, isThumbnailOnlyVideo, resolveDownloadableAssetUrl } from '../../../utils'

import type { ContentItemDownloadRequest } from './content-item-details.types'
import DeletableCampaignItem from './DeletableCampaignItem.vue'

const CAROUSEL_DESKTOP_WIDTH = 360
const CAROUSEL_DESKTOP_HEIGHT = 640
const CAROUSEL_MOBILE_WIDTH = 320
const CAROUSEL_MOBILE_HEIGHT = 569

// TODO: Show content data when backend supports it.
// For now, we hide it to avoid confusion as the data is not correct.
// We will enable it once we have the correct data from the backend.
const showContentData = false

type ViewMode = 'admin' | 'advertiser'
const props = defineProps<{
  data: ContentItemLinkDetails
  contentItemId: string
  viewMode: ViewMode
  isDownloading?: boolean
  loadingAction?: ContentItemStatusActionEnum | null
  hideCosts?: boolean
  hideOrders?: boolean
  affiliateLinkMetrics?: AffiliateLinkMetrics
}>()

type CarouselItem = {
  id: string | number
  asset: Asset
  label: string
}

const { formatNumber, formatDateTimeLong, formatMoney } = inject(formatterProviderKey, defaultFormatter)
const contentItemOverviewLinks = inject(linksProviderKey)

const emits = defineEmits<{
  (event: 'onClose'): void
  (event: 'downloadContent', request: ContentItemDownloadRequest): void
  (event: 'openAddCampaignModal'): void
  (event: 'openRemoveCampaignModal', campaignId: number): void
  (event: 'contentItemChange', contentItemId: string): void
  (event: 'approveContent', contentItemId: string): void
  (event: 'rejectContent', contentItemId: string): void
}>()
const activeContentItem = ref(
  props.data?.contentItems.find((item: ContentItemLinkDetails['contentItems'][0]) => item.id === props.contentItemId),
)

watch(
  () => {
    const item = props.data?.contentItems.find((ci) => ci.id === activeContentItem.value?.id)
    return item?.status
  },
  (newStatus) => {
    if (newStatus && activeContentItem.value && activeContentItem.value.status !== newStatus) {
      activeContentItem.value.status = newStatus
    }
  },
)

const formatInteger = (value: number | null | undefined, showZero = false) => {
  if (value == null || (!value && !showZero)) {
    return '—'
  }
  return formatNumber(Number(value))
}

const formatPercent = (value: number | null | undefined, showZero = false) => {
  if (value == null || (!value && !showZero)) {
    return '—'
  }
  return `${formatNumber(Number(value))}%`
}

type MetricRow = { label: string; value: string | number; tooltip?: { title?: string; description: string } }

const activeContentItemMetrics = computed<MetricRow[] | null>(() => {
  const contentItem = activeContentItem.value
  if (!contentItem?.metrics) {
    return null
  }

  const {
    views,
    estimatedViews,
    uniqueViews,
    estimatedUniqueViews,
    estimatedEngagementRate,
    engagementRate,
    mediaValue,
    estimatedMediaValue,
    clickThroughRate,
    estimatedClickThroughRate,
  } = contentItem.metrics

  const metrics: MetricRow[] = []
  if (views != null) {
    metrics.push({ label: 'Views', value: formatInteger(views, true) })
  }
  if (estimatedViews != null) {
    metrics.push({
      label: 'Est. views',
      value: formatInteger(estimatedViews, true),
      tooltip: {
        title: 'Estimated views',
        description: "Estimated based on creator's performance.",
      },
    })
  }
  if (uniqueViews != null) {
    metrics.push({ label: 'Unique views', value: formatInteger(uniqueViews, true) })
  }
  if (estimatedUniqueViews != null) {
    metrics.push({
      label: 'Est. unique views',
      value: formatInteger(estimatedUniqueViews, true),
      tooltip: {
        title: 'Estimated unique views',
        description: "Estimated based on creator's performance.\nAlso known as 'Reach' by Instagram.",
      },
    })
  }
  if (clickThroughRate != null) {
    metrics.push({
      label: 'Click-through rate',
      value: formatPercent(clickThroughRate, true),
      tooltip: {
        title: 'Click-through rate',
        description: 'Percentage of clicks out of total views.',
      },
    })
  }
  if (estimatedClickThroughRate != null) {
    metrics.push({ label: 'Est. click-through rate', value: formatPercent(estimatedClickThroughRate, true) })
  }
  if (engagementRate != null) {
    metrics.push({ label: 'Engagement rate', value: formatPercent(engagementRate, true) })
  }
  if (estimatedEngagementRate != null) {
    metrics.push({ label: 'Est. engagement rate', value: formatPercent(estimatedEngagementRate, true) })
  }
  if (mediaValue != null) {
    metrics.push({ label: 'Media value', value: formatMoney(mediaValue) })
  }
  if (estimatedMediaValue != null) {
    metrics.push({
      label: 'Est. media value',
      value: formatMoney(estimatedMediaValue),
      tooltip: {
        title: 'Estimated media value',
        description: "The estimated monetary worth of your content's exposure.\n(Est. Views/1000)*CPM",
      },
    })
  }

  return metrics.length > 0 ? metrics : null
})

const carouselItems = computed<CarouselItem[]>(() => {
  if (!props.data?.contentItems) {
    return []
  }

  const items = props.data.contentItems.map((contentItem) => ({
    id: contentItem.id,
    asset: contentItem.assets[0]!,
    label: formatDateTimeLong(contentItem.createdAt),
  }))

  return items
})

const updateActiveContentItem = (item: CarouselItem) => {
  activeContentItem.value = props.data.contentItems.find((contentItem) => contentItem.id === item.id)
  emits('contentItemChange', String(item.id))
}

const creatorLink = computed(() => {
  if (!props.data.creator) {
    return null
  }

  if (props.viewMode === 'admin') {
    return `/creators/${props.data.creator.id}/info`
  }

  return contentItemOverviewLinks?.getCreatorLink(props.data.creator.id) ?? null
})

const creatorLinkComponent = computed(() => {
  if (!creatorLink.value) {
    return null
  }

  if (props.viewMode === 'admin') {
    return 'router-link'
  }

  return isInternalLink(creatorLink.value) ? 'router-link' : 'a'
})

const creatorLinkProps = computed(() => {
  if (!creatorLink.value) {
    return {}
  }

  return creatorLinkComponent.value === 'router-link' ? { to: creatorLink.value } : { href: creatorLink.value }
})

const advertiserLink = computed(() => {
  if (!props.data.advertiser) {
    return null
  }

  return contentItemOverviewLinks?.getAdvertiserLink(props.data.advertiser?.id)
})

// Metrics are not present only when the feature flag is off. When it's off, the metrics are shown from the social-media-api endpoint.
const orderValue = computed(() =>
  props.affiliateLinkMetrics ? props.affiliateLinkMetrics.orderValue : props.data.affiliateLinkStats?.orderValue,
)
const numberOfClicks = computed(() =>
  props.affiliateLinkMetrics ? props.affiliateLinkMetrics.clicks : props.data.affiliateLinkStats?.numberOfClicks,
)
const numberOfOrders = computed(() =>
  props.affiliateLinkMetrics ? props.affiliateLinkMetrics.numberOfOrders : props.data.affiliateLinkStats?.numberOfOrders,
)

const estimatedCostsRow = computed(() => {
  const metrics = props.affiliateLinkMetrics

  if (props.viewMode === 'admin') {
    return {
      label: 'Est. creator costs',
      value: metrics ? metrics.costCreatorEstimated : props.data.affiliateLinkStats?.estimatedCreatorCosts,
    }
  }

  if (props.hideCosts) {
    return null
  }

  return {
    label: 'Est. costs',
    value: metrics ? metrics.revenueInvoiceEstimated : props.data.affiliateLinkStats?.estimatedAdvertiserCosts,
  }
})

const linkFinanceMetricRows = computed<MetricRow[]>(() => {
  if (!props.affiliateLinkMetrics) {
    return []
  }

  const { roas, ers, avgCostPerClick } = deriveAffiliateLinkKpis(props.affiliateLinkMetrics, props.viewMode)
  const costLabel = props.viewMode === 'admin' ? 'estimated creator cost' : 'estimated cost'
  const costLabelCapitalized = props.viewMode === 'admin' ? 'Estimated creator cost' : 'Estimated cost'
  const rows: MetricRow[] = []

  if (!props.hideOrders && estimatedCostsRow.value) {
    rows.push({
      label: props.viewMode === 'admin' ? 'Metapic ROAS' : 'ROAS',
      value: formatLongValue(roas, 'number'),
      tooltip: {
        title: 'Return on ad spend',
        description: `Order value divided by ${costLabel}.`,
      },
    })
    rows.push({
      label: props.viewMode === 'admin' ? 'Metapic ERS' : 'ERS',
      value: formatLongValue(ers, 'percentage'),
      tooltip: {
        title: 'Effective revenue share',
        description: `${costLabelCapitalized} divided by order value.`,
      },
    })
  }

  if (estimatedCostsRow.value) {
    rows.push({
      label: 'Avg. cost per click',
      value: formatMoney(avgCostPerClick),
      tooltip: {
        title: 'Average cost per click',
        description: `${costLabelCapitalized} divided by the number of clicks.`,
      },
    })
  }

  return rows
})

const showOrdersAndCostsSection = computed(
  () => !props.hideOrders || Boolean(estimatedCostsRow.value) || linkFinanceMetricRows.value.length > 0 || showContentData,
)

const getCampaignLink = (campaignId: string | number) => contentItemOverviewLinks?.getCampaignLink(campaignId)
const campaigns = computed(() => props.data.campaigns ?? [])

const getCampaignDisplayName = (campaign: NonNullable<ContentItemLinkDetails['campaigns']>[number]) => {
  if (props.viewMode === 'advertiser') {
    return campaign.title || `Campaign #${campaign.id}`
  }
  return campaign.name || campaign.title || `Campaign #${campaign.id}`
}
const hasCampaigns = computed(() => campaigns.value.length > 0)
const canAddCampaign = computed(() => props.viewMode === 'admin' && Boolean(props.data.advertiser) && Boolean(props.data.affiliateLink))
const showNoCampaigns = computed(() => !hasCampaigns.value && !canAddCampaign.value)

const openAddCampaignModal = () => {
  emits('openAddCampaignModal')
}

const handleDeleteCampaign = (campaignId: number) => {
  emits('openRemoveCampaignModal', campaignId)
}

const activeAsset = computed(() => activeContentItem.value?.assets[0])
const canDownload = computed(() => Boolean(activeAsset.value?.url || activeAsset.value?.thumbnailUrl))

const download = () => {
  const asset = activeAsset.value
  if (!activeContentItem.value || !asset) {
    return
  }
  const url = resolveDownloadableAssetUrl(asset)
  if (!url) {
    return
  }
  emits('downloadContent', {
    contentItemId: String(activeContentItem.value.id),
    assetId: asset.type === 'video' && !isThumbnailOnlyVideo(asset) ? asset.id : undefined,
    url,
  })
}

const handleApprove = () => {
  if (!activeContentItem.value) {
    return
  }
  emits('approveContent', String(activeContentItem.value.id))
}

const handleReject = () => {
  if (!activeContentItem.value) {
    return
  }
  emits('rejectContent', String(activeContentItem.value.id))
}

type FooterButtonAction = ContentItemStatusActionEnum | 'download'

const handleAction = (action: FooterButtonAction) => {
  if (action === ContentItemStatusActionEnum.Approve) {
    handleApprove()
  } else if (action === ContentItemStatusActionEnum.Reject) {
    handleReject()
  } else {
    download()
  }
}

const actionButtons = computed(() => {
  const status = activeContentItem.value?.status as ContentItemStatusEnum
  return [
    {
      action: ContentItemStatusActionEnum.Approve as FooterButtonAction,
      icon: ThumbsUpIcon,
      label: status === ContentItemStatusEnum.Approved ? 'Approved' : 'Approve',
      isLoading: props.loadingAction === ContentItemStatusActionEnum.Approve,
      isDisabled: false,
      cssClass: status === ContentItemStatusEnum.Approved ? 'content-item-overview__approve-button--active' : '',
      adminOnly: true,
    },
    {
      action: ContentItemStatusActionEnum.Reject as FooterButtonAction,
      icon: ThumbsDownIcon,
      label: status === ContentItemStatusEnum.Rejected ? 'Rejected' : 'Reject',
      isLoading: props.loadingAction === ContentItemStatusActionEnum.Reject,
      isDisabled: false,
      cssClass: status === ContentItemStatusEnum.Rejected ? 'content-item-overview__reject-button--active' : '',
      adminOnly: true,
    },
    {
      action: 'download' as FooterButtonAction,
      icon: DownloadIcon,
      label: 'Download',
      isLoading: props.isDownloading ?? false,
      isDisabled: !canDownload.value,
      cssClass: '',
      adminOnly: false,
    },
  ].filter((b) => !b.adminOnly || props.viewMode === 'admin')
})

const { isMobileView } = useMobileView()
</script>
<template>
  <FlexComponent direction="column" class="content-item-overview__drawer">
    <FlexComponent class="content-item-overview__layout" flex="1">
      <BoxComponent w="100%" class="content-item-overview__carousel-container">
        <CarouselSlider
          :items="carouselItems"
          :active-item-id="contentItemId"
          :width="isMobileView ? CAROUSEL_MOBILE_WIDTH : CAROUSEL_DESKTOP_WIDTH"
          :height="isMobileView ? CAROUSEL_MOBILE_HEIGHT : CAROUSEL_DESKTOP_HEIGHT"
          @change="updateActiveContentItem"
        />
        <ButtonComponent
          v-if="isMobileView"
          class="content-item-overview__close-button mobile-only"
          variant="transparent"
          is-icon
          aria-label="Close"
          @click="emits('onClose')"
        >
          <ElIcon :size="24" color="var(--secondary-64)">
            <CloseIcon />
          </ElIcon>
        </ButtonComponent>
      </BoxComponent>
      <BoxComponent :p="24" class="content-item-overview__content-container">
        <BoxComponent>
          <BoxComponent :mb="24">
            <FlexComponent align="center" justify="space-between">
              <TypographyComponent variant="headline-medium">Content details</TypographyComponent>
              <ButtonComponent
                class="content-item-overview__close-button"
                variant="transparent"
                is-icon
                aria-label="Close"
                @click="emits('onClose')"
              >
                <ElIcon :size="24" color="var(--secondary-64)">
                  <CloseIcon />
                </ElIcon>
              </ButtonComponent>
            </FlexComponent>
          </BoxComponent>
          <BoxComponent :mb="24">
            <TypographyComponent variant="headline-small">Content Data</TypographyComponent>
          </BoxComponent>
          <FlexComponent v-if="activeContentItem" :gap="16" direction="column">
            <FlexComponent :gap="8" align="center" justify="space-between">
              <TypographyComponent variant="title-medium" :font-weight="600">Source</TypographyComponent>
              <PlatformHandle
                :handle="activeContentItem.profileHandle"
                :platform="activeContentItem.platform"
                class="content-item-overview__source"
              />
            </FlexComponent>
            <FlexComponent :gap="8" align="center" justify="space-between">
              <TypographyComponent variant="title-medium" :font-weight="600">Post date</TypographyComponent>
              <TypographyComponent variant="body-medium">
                {{ formatDateTimeLong(activeContentItem.createdAt) }}
              </TypographyComponent>
            </FlexComponent>
            <template v-if="activeContentItemMetrics">
              <FlexComponent v-for="metric in activeContentItemMetrics" :key="metric.label" :gap="8" align="center" justify="space-between">
                <FlexComponent :gap="4" align="center">
                  <TypographyComponent variant="title-medium" :font-weight="600">{{ metric.label }}</TypographyComponent>
                  <TooltipComponent v-if="metric.tooltip" placement="top">
                    <button type="button" class="content-item-overview__metric-help" :aria-label="metric.tooltip.description">
                      <ElIcon :size="16">
                        <HelpIcon />
                      </ElIcon>
                    </button>
                    <template #content>
                      <FlexComponent direction="column" :gap="4">
                        <TypographyComponent v-if="metric.tooltip.title" variant="title-medium">
                          {{ metric.tooltip.title }}
                        </TypographyComponent>
                        <TypographyComponent variant="body-medium">{{ metric.tooltip.description }}</TypographyComponent>
                      </FlexComponent>
                    </template>
                  </TooltipComponent>
                </FlexComponent>
                <TypographyComponent variant="body-medium">{{ metric.value }}</TypographyComponent>
              </FlexComponent>
            </template>
          </FlexComponent>
          <el-divider />
        </BoxComponent>
        <BoxComponent>
          <BoxComponent :mb="24">
            <TypographyComponent variant="headline-small">Overview</TypographyComponent>
          </BoxComponent>
          <FlexComponent :gap="16" direction="column">
            <FlexComponent :gap="8" align="center" justify="space-between">
              <TypographyComponent variant="title-medium" :font-weight="600">Creator</TypographyComponent>
              <component :is="creatorLinkComponent" v-if="data.creator && creatorLink" v-bind="creatorLinkProps">
                <TypographyComponent variant="body-medium" text-decoration="underline" cursor="pointer">
                  {{ data.creator.username }}
                </TypographyComponent>
              </component>
              <TypographyComponent v-else-if="data.creator" variant="body-medium">
                {{ data.creator.username }}
              </TypographyComponent>
              <TypographyComponent v-else variant="body-medium">{{ '—' }}</TypographyComponent>
            </FlexComponent>
            <FlexComponent v-if="viewMode !== 'advertiser'" :gap="8" align="center" justify="space-between">
              <TypographyComponent variant="title-medium" :font-weight="600">Advertiser</TypographyComponent>
              <router-link v-if="data.advertiser && advertiserLink && isInternalLink(advertiserLink)" :to="advertiserLink">
                <TypographyComponent variant="body-medium" text-decoration="underline" cursor="pointer">
                  {{ data.advertiser.name }}
                </TypographyComponent>
              </router-link>
              <a v-else-if="data.advertiser && advertiserLink" :href="advertiserLink">
                <TypographyComponent variant="body-medium" text-decoration="underline" cursor="pointer">
                  {{ data.advertiser.name }}
                </TypographyComponent>
              </a>
              <TypographyComponent v-else-if="data.advertiser" variant="body-medium">
                {{ data.advertiser.name }}
              </TypographyComponent>
              <TypographyComponent v-else variant="body-medium">{{ '—' }}</TypographyComponent>
            </FlexComponent>
            <FlexComponent :gap="8" align="top" justify="space-between" class="content-item-campaign-row">
              <TypographyComponent variant="title-medium" :font-weight="600">
                {{ campaigns.length > 1 ? 'Campaigns' : 'Campaign' }}
              </TypographyComponent>
              <FlexComponent direction="column" align="stretch" grow="1" :gap="8">
                <BoxComponent v-for="campaign in campaigns" :key="campaign.id" :title="getCampaignDisplayName(campaign)">
                  <DeletableCampaignItem
                    :campaign-id="campaign.id"
                    :campaign-link="getCampaignLink(campaign.id)"
                    :is-internal-link="isInternalLink(getCampaignLink(campaign.id))"
                    :is-deletable="viewMode === 'admin'"
                    @delete="handleDeleteCampaign"
                  >
                    {{ getCampaignDisplayName(campaign) }}
                  </DeletableCampaignItem>
                </BoxComponent>
                <TypographyComponent
                  :is="'button'"
                  v-if="canAddCampaign"
                  variant="body-medium"
                  text-align="right"
                  type="button"
                  cursor="pointer"
                  class="content-item-overview__add-campaign-button"
                  @click="openAddCampaignModal"
                >
                  + Add campaign
                </TypographyComponent>
                <TypographyComponent v-if="showNoCampaigns" variant="body-medium" text-align="right">{{ '—' }}</TypographyComponent>
              </FlexComponent>
            </FlexComponent>
            <FlexComponent :gap="8" align="center" justify="space-between">
              <TypographyComponent variant="title-medium" :font-weight="600">Link</TypographyComponent>
              <a
                v-if="data.affiliateLink"
                :href="data.affiliateLink.originalUrl"
                class="content-item-overview__link content-item-overview__external-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <TypographyComponent variant="body-medium" text-decoration="underline" cursor="pointer">
                  {{ data.affiliateLink.id }}
                </TypographyComponent>
                <ElIcon color="var(--primary)" class="icon">
                  <ExternalLinkIcon />
                </ElIcon>
              </a>
              <TypographyComponent v-else variant="body-medium">—</TypographyComponent>
            </FlexComponent>
            <FlexComponent :gap="8" align="center" justify="space-between">
              <TypographyComponent variant="title-medium" :font-weight="600">Clicks</TypographyComponent>
              <TypographyComponent variant="body-medium">{{ numberOfClicks || '—' }}</TypographyComponent>
            </FlexComponent>
          </FlexComponent>
          <el-divider />
        </BoxComponent>
        <BoxComponent v-if="showOrdersAndCostsSection" :pb="24">
          <BoxComponent :mb="24">
            <TypographyComponent variant="headline-small">Orders & costs</TypographyComponent>
          </BoxComponent>
          <FlexComponent :gap="16" direction="column">
            <FlexComponent v-if="!hideOrders" :gap="8" align="center" justify="space-between">
              <TypographyComponent variant="title-medium" :font-weight="600">Order value</TypographyComponent>
              <TypographyComponent variant="body-medium">{{ formatMoney(orderValue) }}</TypographyComponent>
            </FlexComponent>
            <FlexComponent v-if="estimatedCostsRow" :gap="8" align="center" justify="space-between">
              <TypographyComponent variant="title-medium" :font-weight="600">{{ estimatedCostsRow.label }}</TypographyComponent>
              <TypographyComponent variant="body-medium">
                {{ formatMoney(estimatedCostsRow.value) }}
              </TypographyComponent>
            </FlexComponent>
            <FlexComponent v-if="!hideOrders" :gap="8" align="center" justify="space-between">
              <TypographyComponent variant="title-medium" :font-weight="600">Orders</TypographyComponent>
              <TypographyComponent variant="body-medium">{{ formatInteger(numberOfOrders) }}</TypographyComponent>
            </FlexComponent>
            <FlexComponent v-for="metric in linkFinanceMetricRows" :key="metric.label" :gap="8" align="center" justify="space-between">
              <FlexComponent :gap="4" align="center">
                <TypographyComponent variant="title-medium" :font-weight="600">{{ metric.label }}</TypographyComponent>
                <TooltipComponent v-if="metric.tooltip" placement="top">
                  <button type="button" class="content-item-overview__metric-help" :aria-label="metric.tooltip.description">
                    <ElIcon :size="16">
                      <HelpIcon />
                    </ElIcon>
                  </button>
                  <template #content>
                    <FlexComponent direction="column" :gap="4">
                      <TypographyComponent v-if="metric.tooltip.title" variant="title-medium">
                        {{ metric.tooltip.title }}
                      </TypographyComponent>
                      <TypographyComponent variant="body-medium">{{ metric.tooltip.description }}</TypographyComponent>
                    </FlexComponent>
                  </template>
                </TooltipComponent>
              </FlexComponent>
              <TypographyComponent variant="body-medium">{{ metric.value }}</TypographyComponent>
            </FlexComponent>
            <FlexComponent v-if="showContentData" :gap="8" align="center" justify="space-between">
              <TypographyComponent variant="title-medium" :font-weight="600">Est. Metapic revenue</TypographyComponent>
              <TypographyComponent variant="body-medium">
                {{ formatMoney(data.affiliateLinkStats?.estimatedMetapicRevenue) }}
              </TypographyComponent>
            </FlexComponent>
          </FlexComponent>
        </BoxComponent>
      </BoxComponent>
    </FlexComponent>
    <FlexComponent v-if="activeContentItem" align="center" justify="center" class="content-item-overview__footer">
      <ButtonComponent
        v-for="button in actionButtons"
        :key="button.action"
        variant="text"
        :is-loading="button.isLoading"
        :is-disabled="button.isDisabled"
        :class="button.cssClass"
        @click="handleAction(button.action)"
      >
        <template #iconLeft>
          <ElIcon :size="18">
            <component :is="button.icon" />
          </ElIcon>
        </template>
        {{ button.label }}
      </ButtonComponent>
    </FlexComponent>
  </FlexComponent>
</template>
<style scoped lang="scss">
.content-item-overview__drawer {
  position: relative;
  height: 100%;
}
.content-item-overview__layout {
  min-height: 0;
  overflow-y: auto;
  @media screen and (max-width: 767px) {
    flex-direction: column;
  }
}

.content-item-overview__carousel-container {
  background-color: var(--button-hover);
  flex-shrink: 0;
  flex-basis: 480px;
  @media screen and (max-width: 767px) {
    min-height: 600px;
  }
}

.content-item-overview__content-container {
  flex: 1;
  min-height: 0;
  @media screen and (min-width: 992px) {
    overflow-y: auto;
  }
}
.content-item-overview__footer {
  background: var(--inverted);
  border-top: 1px solid var(--secondary-12);
  padding: 8px 16px;

  @media screen and (max-width: 767px) {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
  }
}
.content-item-overview__link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
:deep(.content-item-overview__external-link) {
  path {
    fill: currentColor;
  }
}
.content-item-overview__add-campaign-button {
  background: none;
  color: var(--secondary-48);
  border: none;
  padding: 0;
  padding-top: 0;

  &:hover {
    text-decoration: underline;
  }
}

.content-item-overview__metric-help {
  display: flex;
  align-items: center;
  padding: 0;
  color: var(--secondary-48, rgba(0, 0, 0, 0.48));
  background: none;
  border: none;
  cursor: help;
}

.content-item-overview__approve-button--active {
  color: var(--success);
  pointer-events: none;

  :deep(svg) {
    path {
      fill: var(--success);
    }
  }
}

.content-item-overview__reject-button--active {
  color: var(--error);
  pointer-events: none;

  :deep(svg) {
    path {
      fill: var(--error);
    }
  }
}

.content-item-overview__close-button {
  transition: all var(--transition) ease;
  &:hover {
    background-color: var(--secondary-12);
    border-radius: 40px;
  }

  @media screen and (max-width: 767px) {
    display: none;

    &.mobile-only {
      display: flex;
      position: absolute;
      top: 8px;
      right: 8px;
    }
  }
}
</style>
